import React, { Suspense, lazy, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { PeriodProvider } from '@/context/PeriodContext';
import LoginPage from '@/pages/LoginPage';
import { ProtectedRoute } from '@/components/ProtectedRoute';

// Lazy load all pages for faster initial load
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const RMPage = lazy(() => import('@/pages/RMPage'));
const FGPage = lazy(() => import('@/pages/FGPage'));
const ProductionPage = lazy(() => import('@/pages/ProductionPage'));
const AttendancePage = lazy(() => import('@/pages/AttendancePage'));
const AuditLogsPage = lazy(() => import('@/pages/AuditLogsPage'));
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'));
const QCPage = lazy(() => import('@/pages/QCPage'));
const EmployeePage = lazy(() => import('@/pages/EmployeePage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const BatchTracePage = lazy(() => import('@/pages/BatchTracePage'));
const OperationsPage = lazy(() => import('@/pages/OperationsPage'));
const FaceScannerPage = lazy(() => import('@/pages/FaceScannerPage'));
const KioskPage = lazy(() => import('@/pages/KioskPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const ApprovalsPage = lazy(() => import('@/pages/ApprovalsPage'));
const LogisticsPage = lazy(() => import('@/pages/LogisticsPage'));

// Minimal loading skeleton
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-bar" />
  </div>
);

// Animated route wrapper
const AnimatedPage = ({ children }) => (
  <div className="page-enter">
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </div>
);

function AppRouter() {
  const location = useLocation();

  // Dynamic page title: KBFPL | <Page Name>
  useEffect(() => {
    const titleMap = {
      '/login': 'Login',
      '/dashboard': 'Dashboard',
      '/rm': 'Raw Materials',
      '/fg': 'Finished Goods',
      '/production': 'Production',
      '/attendance': 'Attendance',
      '/audit-logs': 'Audit & Backup',
      '/users': 'User Management',
      '/qc': 'Quality Control',
      '/employees': 'Staff & Attendance',
      '/calendar': 'Factory Calendar',
      '/billing': 'Billing',
      '/operations': 'Seasoning Operations',
      '/profile': 'Profile',
      '/face-scanner': 'AI Face Scanner',
      '/kiosk': 'Kiosk',
      '/approvals': 'Approvals',
    };
    const pageName = titleMap[location.pathname] || '';
    document.title = pageName ? `KBFPL | ${pageName}` : 'KBFPL';
  }, [location.pathname]);

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><AnimatedPage><DashboardPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/rm" element={<ProtectedRoute><AnimatedPage><RMPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/fg" element={<ProtectedRoute><AnimatedPage><FGPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/production" element={<ProtectedRoute><AnimatedPage><ProductionPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute><AnimatedPage><AttendancePage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AnimatedPage><AuditLogsPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><AnimatedPage><UserManagementPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/qc" element={<ProtectedRoute><AnimatedPage><QCPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><AnimatedPage><EmployeePage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><AnimatedPage><CalendarPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><AnimatedPage><BillingPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/trace" element={<ProtectedRoute><AnimatedPage><BatchTracePage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/operations" element={<ProtectedRoute><AnimatedPage><OperationsPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AnimatedPage><ProfilePage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute><AnimatedPage><ApprovalsPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/logistics" element={<ProtectedRoute><AnimatedPage><LogisticsPage /></AnimatedPage></ProtectedRoute>} />
      <Route path="/face-scanner" element={<ProtectedRoute><FaceScannerPage /></ProtectedRoute>} />
      <Route path="/kiosk" element={<Suspense fallback={<PageLoader />}><KioskPage /></Suspense>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PeriodProvider>
            <AppRouter />
          </PeriodProvider>
          <Toaster
            position="top-center"
            richColors
            closeButton
            duration={2000}
            toastOptions={{
              style: {
                background: '#FFFFFF',
                border: '2px solid #09090B',
                borderRadius: 0,
                fontWeight: 600,
                fontFamily: 'IBM Plex Sans, sans-serif',
                padding: '12px 16px',
                minWidth: '250px',
                maxWidth: '90vw',
                fontSize: '13px'
              },
              className: 'toaster-custom'
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
